/**
 * Next.js internal render-bailout / control-flow signal classifier.
 *
 * ROOT-CAUSE: Next.js throws a handful of internal `Error` subclasses to
 * signal control flow through the render tree (redirect, not-found, "please
 * bail to client-side rendering", "please bail to dynamic rendering") rather
 * than to report an application failure. Every one of these carries a fixed
 * `.digest` and/or a fixed message prefix. When application code wraps a
 * `try { ... } catch (error) { logServerActionError(...) }` around code that
 * (transitively) calls `cookies()`/`headers()`/`connection()` — e.g. via
 * `getAuthContext()` inside a shared action scaffold — a control-flow signal
 * gets caught by the generic catch and mistaken for a real bug.
 *
 * CONCRETE INCIDENT (2026-07-15): `/lead-sharing` and `/settings/import-templates`
 * called server actions that read cookies (auth context) during Next's
 * static-render attempt. Next threw `DynamicServerError` (digest
 * `DYNAMIC_SERVER_USAGE`, message `Dynamic server usage: Route /lead-sharing
 * couldn't be rendered statically because it used \`cookies\`. See more info
 * here: https://nextjs.org/docs/messages/dynamic-server-error` — verified
 * against node_modules/next/dist/client/components/hooks-server-context.js).
 * The action's own catch block ALSO had a broken "silent" branch (matched
 * `.includes('prerender')`, a substring the real message never contains — so
 * it always fell through) that still called `logServerActionError()`. Every
 * hit persisted a row to `error_reports`: 1,728 rows total, 145 in the last 7
 * days, drowning real application errors.
 *
 * CLASS: next-control-flow-signal-reported-as-application-error. This module
 * is the single source of truth for "is this Error actually Next.js internal
 * control flow?" across every error-persistence choke point:
 *   - src/lib/utils/error-logger.ts (logServerActionError / logAPIRouteError /
 *     logServerActionErrorWithContext — server-side persistence)
 *   - src/lib/error-reporting/client/reporter.ts (captureClientError —
 *     client-side persistence; a server-render digest can reach the client
 *     via error.tsx/global-error.tsx's `error` prop)
 *   - src/app/api/error-reports/route.ts (POST ingestion — belt-and-braces
 *     for any caller that bypasses the client-side guard, e.g. a stale bundle)
 *
 * NEXT_REDIRECT is already handled by `isRedirectError()` (next/dist) at each
 * server-side choke point with its own rethrow-vs-swallow semantics (Rule
 * #33 / check-next-redirect-leak) — those call sites are UNCHANGED by this
 * module. NEXT_REDIRECT is still recognised here so any OTHER caller (the
 * client reporter, the ingestion route) that has no separate redirect
 * handling gets the same "do not persist" behavior for free.
 *
 * Digest values verified against the installed Next.js 16.2.9 sources:
 *   - DYNAMIC_SERVER_USAGE            node_modules/next/dist/client/components/hooks-server-context.js
 *   - BAILOUT_TO_CLIENT_SIDE_RENDERING node_modules/next/dist/shared/lib/lazy-dynamic/bailout-to-csr.js
 *   - NEXT_REDIRECT;...               node_modules/next/dist/client/components/redirect-error.js
 *   - NEXT_HTTP_ERROR_FALLBACK;404    node_modules/next/dist/client/components/http-access-fallback/http-access-fallback.js
 *     (notFound()'s CURRENT digest — legacy/older Next versions used a bare
 *     `NEXT_NOT_FOUND` digest; both are matched for forward/backward safety.)
 *
 * NARROW BY DESIGN: only matches the fixed framework digests/message prefixes
 * above. A genuine application error thrown from inside a Server Component or
 * Server Action never carries these digests and is unaffected.
 *
 * Related: [[rules/enforcements]] (#33, #49) [[knowledge/bug-patterns]]
 */

function readDigest(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const digest = (error as { digest?: unknown }).digest;
  return typeof digest === 'string' ? digest : undefined;
}

function readMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    return typeof message === 'string' ? message : '';
  }
  return '';
}

/** Digest prefixes/values that mean "this is Next.js control flow, not a bug". */
const CONTROL_FLOW_DIGEST_MARKERS = [
  'DYNAMIC_SERVER_USAGE',
  'BAILOUT_TO_CLIENT_SIDE_RENDERING',
  'NEXT_REDIRECT',
  'NEXT_NOT_FOUND', // legacy digest shape (pre-App-Router-fallback unification)
  'NEXT_HTTP_ERROR_FALLBACK', // current notFound()/forbidden()/unauthorized() digest
];

/** Message substrings that mean the same thing when a digest is unavailable
 * (e.g. after crossing a serialization boundary that dropped it). */
const CONTROL_FLOW_MESSAGE_MARKERS = [
  'Dynamic server usage',
  'DYNAMIC_SERVER_USAGE',
  'BAILOUT_TO_CLIENT_SIDE_RENDERING',
  'NEXT_REDIRECT',
  'NEXT_NOT_FOUND',
];

/**
 * True when `error` is a Next.js internal render-bailout / control-flow
 * signal (redirect, not-found, static-bailout, client-side-render-bailout)
 * rather than an application bug. Callers that persist errors (Sentry, DB)
 * MUST skip persistence entirely when this returns true — it is not merely
 * "less severe", it is not an error at all.
 */
export function isNextControlFlowError(error: unknown): boolean {
  if (error == null) return false;

  const digest = readDigest(error);
  if (digest && CONTROL_FLOW_DIGEST_MARKERS.some((marker) => digest.startsWith(marker))) {
    return true;
  }

  const message = readMessage(error);
  if (!message) return false;
  return CONTROL_FLOW_MESSAGE_MARKERS.some((marker) => message.includes(marker));
}
